import { html, LitElement } from 'lit';
import { TagKey } from '@cof/tag-catalog-fs-personal-mfe';
import { ApplicantsService } from '../../../services/applicants.service.js';
import { DebugService } from '../../../shared/services/debug.service.js';
import { SessionService } from '../../../core/services/session/session.service.js';
import { REFI_PERSONAL_CONST } from '../../../shared/constants/app.constants.js';
import { stratum } from '../../../../constants/stratum.js';
import { ApplicationService } from '../../../services/application.service';

class RefiAddressReview extends LitElement {
  constructor() {
    super();
    this.primaryAppType = REFI_PERSONAL_CONST.APPLICANT_TYPE.PRIMARY_APPLICANT;
    this.coAppType = REFI_PERSONAL_CONST.APPLICANT_TYPE.CO_APPLICANT;
    this.isPrimary = true;
    this.cardId = 'primary-address-card';
    this.debug = new DebugService('RefiAddressReview');
  }

  static properties = {
    applicantType: { type: String, attribute: 'applicant-type' },
    addressLine1: { type: String },
    addressLine2: { type: String },
    city: { type: String },
    stateCode: { type: String },
    zipCode: { type: String },
    experiments: { type: Object, reflect: true },
    v2Routing: {
      type: Boolean,
      attribute: 'v2-routing',
      reflect: true,
      hasChanged: (newVal) => {
        ApplicationService.isV2Routing = newVal;
      },
    },
  };

  async connectedCallback() {
    super.connectedCallback();
    const eAppId = SessionService.session?.eAppId;

    try {
      await ApplicantsService.getApplicantInformation(eAppId).then(
        (response) => {
          // Set isPrimary and cardId based on applicantType
          this.isPrimary = this.applicantType === this.primaryAppType;
          this.cardId = this.isPrimary ? this.cardId : 'co-address-card';

          const applicantSearchString = this.isPrimary
            ? 'Applicant'
            : 'CoApplicant';
          const applicant = response.applicants.find(
            (applicant) => applicant.applicantType === applicantSearchString
          );

          if (
            !applicant.address.addressLine1 ||
            !applicant.address.city ||
            !applicant.address.stateCode ||
            !applicant.address.postalCode
          ) {
            this.debug.log('Missing applicant address field');
            stratum.publishTag(TagKey.PERSONAL_CHECKOUT_PERSONAL_MFE_LOAD, {
              replacements: {
                value: 'address mfe - failed',
              },
            });
            this.dispatchFailedToLoadOrUpdateEvent();
          } else {
            stratum.publishTag(TagKey.PERSONAL_CHECKOUT_PERSONAL_MFE_LOAD, {
              replacements: {
                value: 'address mfe - successful',
              },
            });
          }

          this.addressLine1 = applicant.address.addressLine1;
          this.addressLine2 = applicant.address.addressLine2;
          this.city = applicant.address.city;
          this.stateCode = applicant.address.stateCode;
          this.zipCode = applicant.address.postalCode;
          this.dispatchCompleteLoadingEvent();
        }
      );

      try {
        await ApplicantsService.getStips(eAppId).then((res) => {
          const residenceStip = res.find(
            (stip) =>
              stip.taskType === REFI_PERSONAL_CONST.STIP_TYPE.PROOF_OF_RESIDENCE
          );
          if (!residenceStip) {
            this.debug.log(
              'Missing Proof of Residence stip info after call to Stips API'
            );
          }
          if (
            residenceStip.taskStatus.toString().toUpperCase() ===
            REFI_PERSONAL_CONST.STIP_INCOMPLETE
          ) {
            this.dispatchEvent(
              new CustomEvent('checkout-update-mudflap', {
                bubbles: true,
                composed: true,
                detail: {
                  actionName: 'update-mudflap',
                  id: this.cardId,
                  text: 'You’ll upload address verification later',
                },
              })
            );
          }
          stratum.publishTag(TagKey.ADDRESS_CHECKOUT_POR_STIP_MUDFLAP_LOADS, {
            replacements: {
              value: `successful - ${residenceStip.taskStatus}`,
            },
          });
        });
      } catch (err) {
        stratum.publishTag(TagKey.ADDRESS_CHECKOUT_POR_STIP_MUDFLAP_LOADS, {
          replacements: {
            value: 'failed',
          },
        });
        this.debug.log(
          'Failed to retrieve stip information for address review on checkout: ',
          err.message
        );
      }
    } catch (err) {
      stratum.publishTag(TagKey.PERSONAL_CHECKOUT_PERSONAL_MFE_LOAD, {
        replacements: {
          value: 'address mfe - failed',
        },
      });
      this.debug.log(
        'Failed to retrieve address information for review on checkout: ',
        err.message
      );
      this.dispatchFailedToLoadOrUpdateEvent();
    }
  }

  dispatchCompleteLoadingEvent() {
    this.dispatchEvent(
      new CustomEvent('checkout-completed-loading', {
        bubbles: true,
        composed: true,
        detail: {
          id: this.cardId,
        },
      })
    );
    this.debug.log('completed loading event fired');
  }

  dispatchFailedToLoadOrUpdateEvent() {
    this.dispatchEvent(
      new CustomEvent('failed-load-or-update', {
        bubbles: true,
        composed: true,
        detail: {
          id: this.cardId,
        },
      })
    );
    this.checkout = 'error';
    this.debug.log('failed to load or update event fired');
  }

  render() {
    return this.addressLine1 && this.city && this.stateCode && this.zipCode
      ? html` <div>
          <fs-body-text variant="primary"
            ><span data-qm-encrypt
              >${this.generateAddressLineText()}</span
            ></fs-body-text
          >
          <fs-body-text variant="primary"
            ><span data-qm-encrypt
              >${this.city}, ${this.stateCode} ${this.zipCode}</span
            ></fs-body-text
          >
        </div>`
      : null;
  }

  generateAddressLineText() {
    return this.addressLine2 && this.addressLine2 !== ''
      ? `${this.addressLine1}, ${this.addressLine2}`
      : `${this.addressLine1}`;
  }
}

customElements.define('refi-address-review', RefiAddressReview);

// Defining it again so that we can import a separate instance for coapp on checkout page
customElements.define(
  'co-refi-address-review',
  class extends RefiAddressReview {}
);
